import "./Css/root.css";
import "./Css/snack.css";
import "./Css/all.css";
import "./Css/admin.css";
import "./Css/app.css";
import "./Css/content.css";
import "./Css/contact.css";
import "./Css/form.css";
import "./Css/footer.css";
import "./Css/menu.css";
import "./Css/TextCombo.tf.css";
import "./Css/ClearableTextInput.tf.css";
import "./Css/markdown.css";

import moment from "moment";
import "moment/locale/pl";
import { NumberInput, TextInput, Button, Select, ModalWindow, Root, MultilineInput, DestroyingPatternContentSwitcher, HashRef, Div, RefBool, HandlersCollection, Line, Span } from "@tblabs/truffle";
import { ProductCategoryPage } from "./Pages/Category/ProductCategoryPage";
import { ProductPage } from "./Pages/Product/ProductPage";
import { RentalPage } from "./Pages/Rental/RentalPage";
import { ContactPage } from "./Pages/Contact/ContactPage";
import { ProductEditPage } from "./Pages/Product/ProductEditPage";
import { ProductsCategoriesEditPage } from "./Pages/Category/ProductsCategoriesEditPage";
import { ProductCategoryEditPage } from "./Pages/Category/ProductCategoryEditPage";
import { ShopConverter } from "./Services/Content/WebsiteContentConverter";
import { ShopContentProvider } from "./Services/Content/ShopContentProvider";
import { LoginPage } from "./Services/Auth/LoginPage";
import { LoginFormLogic } from "./Services/Auth/LoginFormLogic";
import { PageEditPage } from "./Pages/Main/PageEditPage";
import { MainPage } from "./Pages/Main/MainPage";
import { Main } from "./Pages/Components/Utils/Main";
import { MiniBasketBox } from "./Components/MiniBasket/MiniBasketBox";
import { NavBar } from "./Pages/Components/NavBar/NavBar";
import { Table } from "@tblabs/truffle/Controls/TableBuilder/Table";
import { EnvDetector } from "./Services/Env/EnvDetector";
import { BasketPage as BasketPage } from "./Components/OrderForm/BasketPage";
import { Notifier } from "./Services/Notifier/Notifier";
import { EmailSender } from "./Services/Notifier/EmailSender";
import { OrderSummaryPage } from "./Components/OrderForm/OrderSummaryBox";
import { AppConfig } from "./AppConfig";
import { Loader } from "./Components/Utils/Loader";
import { Hero } from "./Pages/Components/Hero";
import { BlogContentConverter } from "./Services/Content/BlogContentConverter";
import { BlogContentProvider } from "./Services/Content/BlogContentProvider";
import { ArticleEditPage } from "./Pages/Blog/ArticleEditPage";
import { BlogPage } from "./Pages/Blog/BlogPage";
import { BlogEditPage } from "./Pages/Blog/BlogEditPage";
import { ArticleViewPage } from "./Pages/Blog/ArticleViewPage";
import { v4 as uuidv4 } from 'uuid';
import { IStorageUser, OnlineStorage } from "@tblabs/storage";
import { BasketProvider } from "./Services/BasketProvider";
import { Ticket } from "./Models/OrderTicket/Ticket";
import { ShopContent } from "./Models/WebsiteContent/ShopContent";
import { BlogContent } from "./Models/Blog/BlogContent";
import { ErrorBox } from "./Pages/Product/InfoBox";
import { Center } from "./Components/Utils/Center";
import { Env } from "./Services/Env/Env";

moment?.locale('pl');

NumberInput.DefaultCssClasses = "";
TextInput.DefaultCssClasses = "";
Button.DefaultCssClasses = "";
Select.DefaultCssClasses = "";
ModalWindow.DefaultCssClasses = "";
ModalWindow.Hook = Root.Hook;
MultilineInput.DefaultCssClasses = "";
Table.DefaultCssClasses = "";

HandlersCollection.MANY_HANDLERS_LIMIT = 10;

(async () =>
{
    const root = new Root("body").Id("root");

    const loader = new Loader("Ładowanie strony...").Margin(32);

    root.Append(
        new Div("app").Append(
            new Main("app__main").Append(
                new Div("app__layout").Append(
                    new Hero(),
                    loader,
                ),
            ),
        ),
    );

    try
    {
        const _env = new EnvDetector();
        const _appConfig = new AppConfig(_env);
        const _emailSender = new EmailSender();
        const _notifier = new Notifier(_emailSender, _appConfig);

        const _storage = new OnlineStorage(_appConfig.StorageUrl, _appConfig.DatabaseName);

        const _shopConverter = new ShopConverter();
        const shopDataset = _storage.GetObjectDataset<ShopContent>("Shop", _shopConverter);
        const _shop = new ShopContentProvider(shopDataset);

        const _blogConverter = new BlogContentConverter();
        const blogDataset = _storage.GetObjectDataset<BlogContent>("Blog", _blogConverter);
        const _blog = new BlogContentProvider(blogDataset);

        const _ticketsDataset = _storage.GetObjectDataset<Ticket>("Tickets");

        await _shop.Load(); // Lepiej to zrobić tutaj bo inaczej każdy komponent który to woła zaciąga sobie asynchronicznie kontent i w efekcie idzie 5 strzałów zamiast jednego
        _shop.Shop.PrepareProducts();
        
        const _basket = new BasketProvider(_shop, _ticketsDataset, _notifier, _appConfig);
        await _basket.Load();

        const _loginLogic = new LoginFormLogic(_storage)
            .OnLogin((user: IStorageUser, autologin: boolean) =>
            {
                window.location.hash = "/";
            })
            .OnLogout(() =>
            {
                _loginLogic.ClearLoginInfo();
                window.location.hash = "/";
            })

        if (_env.Env != Env.Prod)
        {
            await _loginLogic.TryAutoLogin();
        }

        const hash = new HashRef().OnChange(() => window.scrollTo(0, 0));

        const content = new DestroyingPatternContentSwitcher(hash)
            .AddContent("product/edit/:productUrl", ({ productUrl }) => new ProductEditPage(_shop, productUrl))
            .AddContent("product/add", () => new ProductEditPage(_shop))
            .AddContent("categories/edit", () => new ProductsCategoriesEditPage(_shop, _blog))
            .AddContent("category/edit/:categoryUrl", ({ categoryUrl }) => new ProductCategoryEditPage(_shop, categoryUrl))
            .AddContent("page/edit", () => new PageEditPage(_shop))
            .AddContent("blog/add", () => new ArticleEditPage(_blog, uuidv4()))
            .AddContent("blog/edit", () => new BlogEditPage(_blog))
            .AddContent("blog/:articleUrl/edit", ({ articleUrl }) => new ArticleEditPage(_blog, articleUrl))
            .AddContent("kontakt", () => new ContactPage(_shop))
            .AddContent("wypozyczalnia", () => new RentalPage(_shop))
            .AddContent("blog", () => new BlogPage(_blog, _shop))
            .AddContent("blog/:url", ({ url }) => new ArticleViewPage(_blog, url))
            .AddContent("kategoria/:categoryName", ({ categoryName }) => new ProductCategoryPage(_shop, _blog, categoryName))
            .AddContent("produkt/:prod", ({ prod }) => new ProductPage(_shop, _basket, prod))
            .AddContent("produkt/:prod/:option", ({ prod, option }) => new ProductPage(_shop, _basket, prod, option))
            .AddContent("koszyk", () => new BasketPage(_basket, _shop))
            .AddContent("koszyk/podsumowanie", () => new OrderSummaryPage(_basket, _shop))
            .AddContent("login", () => new LoginPage(_loginLogic))
            .AddDefaultContent(() => new MainPage(_shop))
            .Refresh()

        const showMiniBasket = new RefBool(false);

        root.Clear()
            .Append(
                new Div("app").Append(
                    new Main("app__main").Append(
                        new Div("app__layout").Append(
                            new NavBar(_shop, _basket, showMiniBasket),
                            new MiniBasketBox(_basket, showMiniBasket),
                            content,
                        ),
                    ),
                ),

                _shop.IsAuthorized && new Div("admin-info").Text("ADMIN MODE @ " + _env.Env),
            );

    }
    catch (error: any) 
    {
        console.error(error);

        root.Clear().Append(
            new Div("app").Append(
                new Main("app__main").Append(
                    new Div("app__layout").Append(
                        new Hero().Opacity(0.5).Blur(3),
                        new ErrorBox("Błąd ładowania strony", error.message),
                        new Center(
                            new Line().Margin(32),
                            new Span("☎️ Skontaktuj się z nami telefonicznie: +48 507 293 714").FontSize(18).Color("#f2f2f2"),
                        ).MarginTopBottom(64)
                    ),
                ),
            ),
        );
    }
    finally
    {
        loader.RemoveSelf();
    }
})();
